import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { withTimeout } from '../utils/supabaseTimeout';

/**
 * Admin Verification Service
 * Provides secure admin status verification with caching and rate limiting
 * Always verifies from database - never trusts client-side only
 */

// Cache for admin status verification
const adminStatusCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Rate limiting for verification requests
const rateLimitMap = new Map();
const RATE_LIMIT_MAX = 10; // Max 10 requests per minute
const RATE_LIMIT_WINDOW = 60 * 1000; // 1 minute window

/**
 * Validate UUID format
 */
function isValidUUID(uuid) {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}

/**
 * Check rate limit for verification requests
 */
function checkRateLimit(userId) {
  if (!userId) return { allowed: false, error: 'User ID required' };

  const now = Date.now();
  const userLimits = rateLimitMap.get(userId) || { count: 0, resetAt: now + RATE_LIMIT_WINDOW };

  // Reset if window expired
  if (now > userLimits.resetAt) {
    userLimits.count = 0;
    userLimits.resetAt = now + RATE_LIMIT_WINDOW;
  }

  // Check if limit exceeded
  if (userLimits.count >= RATE_LIMIT_MAX) {
    return {
      allowed: false,
      error: `Rate limit exceeded. Maximum ${RATE_LIMIT_MAX} verification requests per minute.`,
      retryAfter: Math.ceil((userLimits.resetAt - now) / 1000),
    };
  }

  // Increment count
  userLimits.count++;
  rateLimitMap.set(userId, userLimits);

  return { allowed: true };
}

/**
 * Get cached admin status
 */
function getCachedStatus(userId) {
  const cached = adminStatusCache.get(userId);
  if (!cached) return null;

  const now = Date.now();
  if (now > cached.expiresAt) {
    // Cache expired
    adminStatusCache.delete(userId);
    return null;
  }

  return cached.data;
}

/**
 * Set cached admin status
 */
function setCachedStatus(userId, isAdmin) {
  adminStatusCache.set(userId, {
    data: { isAdmin, verifiedAt: Date.now() },
    expiresAt: Date.now() + CACHE_TTL,
  });
}

/**
 * Invalidate cache for a user
 */
export function invalidateAdminCache(userId) {
  if (userId) {
    adminStatusCache.delete(userId);
  } else {
    // Clear all cache
    adminStatusCache.clear();
  }
}

/**
 * Verify admin status from database
 * Always checks database - never trusts client-side only
 */
export async function verifyAdminStatus(userId) {
  // Validate input
  if (!userId) {
    return {
      isAdmin: false,
      error: 'User ID is required',
      fromCache: false,
    };
  }

  if (!isValidUUID(userId)) {
    return {
      isAdmin: false,
      error: 'Invalid user ID format',
      fromCache: false,
    };
  }

  if (!isSupabaseConfigured) {
    return {
      isAdmin: false,
      error: 'Supabase is not configured',
      fromCache: false,
    };
  }

  // Check cache first
  const cached = getCachedStatus(userId);
  if (cached) {
    return {
      ...cached,
      fromCache: true,
    };
  }

  // Check rate limit
  const rateLimitCheck = checkRateLimit(userId);
  if (!rateLimitCheck.allowed) {
    // If rate limited, return cached result if available (even if expired)
    const expiredCache = adminStatusCache.get(userId);
    if (expiredCache) {
      return {
        ...expiredCache.data,
        fromCache: true,
        rateLimited: true,
        retryAfter: rateLimitCheck.retryAfter,
      };
    }
    return {
      isAdmin: false,
      error: rateLimitCheck.error,
      fromCache: false,
      rateLimited: true,
      retryAfter: rateLimitCheck.retryAfter,
    };
  }

  try {
    // Query database directly with timeout
    const VERIFY_TIMEOUT_MS = 10000; // 10 seconds
    const queryPromise = supabase
      .from('user_profiles')
      .select('role')
      .eq('id', userId)
      .single();

    const { data, error } = await withTimeout(
      queryPromise,
      VERIFY_TIMEOUT_MS,
      'verifyAdminStatus'
    );

    if (error) {
      console.error('[Admin Verification] Error verifying admin status:', {
        error: error.message || error,
        code: error.code,
        details: error.details,
        hint: error.hint,
        userId,
      });
      return {
        isAdmin: false,
        error: error.message || 'Failed to verify admin status',
        fromCache: false,
      };
    }

    const isAdmin = data?.role === 'admin';

    // Cache the result
    setCachedStatus(userId, isAdmin);

    // Log for debugging (always log in development, or if admin status is true)
    if (import.meta.env.DEV || isAdmin) {
      console.log(`[Admin Verification] User ${userId}: ${isAdmin ? '✅ ADMIN' : '❌ NOT ADMIN'}`, {
        role: data?.role,
        fromCache: false,
        timestamp: new Date().toISOString(),
      });
    }

    return {
      isAdmin,
      role: data?.role || 'user',
      verifiedAt: Date.now(),
      fromCache: false,
    };
  } catch (error) {
    console.error('Error verifying admin status:', error);
    return {
      isAdmin: false,
      error: error.message || 'Unexpected error verifying admin status',
      fromCache: false,
    };
  }
}

/**
 * Get user role from database
 */
export async function getUserRole(userId) {
  if (!userId || !isValidUUID(userId)) {
    return { success: false, error: 'Invalid user ID', role: null };
  }

  if (!isSupabaseConfigured) {
    return { success: false, error: 'Supabase is not configured', role: null };
  }

  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', userId)
      .single();

    if (error) {
      return { success: false, error: error.message, role: null };
    }

    return {
      success: true,
      role: data?.role || 'user',
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to get user role',
      role: null,
    };
  }
}

/**
 * Force refresh admin status (bypasses cache)
 */
export async function refreshAdminStatus(userId) {
  // Invalidate cache first
  invalidateAdminCache(userId);
  
  // Verify from database
  return verifyAdminStatus(userId);
}

/**
 * Get cache statistics (for debugging)
 */
export function getCacheStats() {
  const now = Date.now();
  let validEntries = 0;
  let expiredEntries = 0;

  adminStatusCache.forEach((value, key) => {
    if (now > value.expiresAt) {
      expiredEntries++;
    } else {
      validEntries++;
    }
  });

  return {
    totalEntries: adminStatusCache.size,
    validEntries,
    expiredEntries,
    cacheTTL: CACHE_TTL,
    rateLimitMax: RATE_LIMIT_MAX,
    rateLimitWindow: RATE_LIMIT_WINDOW,
  };
}
